import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  FaUserGraduate,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaFilter,
  FaEye,
  FaLayerGroup,
  FaTimes,
  FaGraduationCap,
  FaHashtag,
  FaIdBadge,
} from "react-icons/fa";
import { toast } from "react-toastify";
import StudentDetails from "../../Components/details/StudentDetails";
import useAuth from "../../useAuth";
import { server } from "../../server";
import { useNavigate } from "react-router-dom";

export default function StudentList() {
  const [items, setItems] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [rollNoFilter, setRollNoFilter] = useState("");
  const [admissionNoFilter, setAdmissionNoFilter] = useState("");
  const { token } = useAuth();
  const navigate = useNavigate();

  const handleViewDetails = async (id) => {
    setSelectedStudentId(id);
    setIsViewOpen(true);
    setDetailsLoading(true);
    try {
      const res = await axios.get(`${server}/students/getById?studentId=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setStudentDetails(res.data.data);
    } catch (error) {
      toast.error("Failed to load details");
      setIsViewOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${server}/students`, {
        params: { search, classFilter },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setItems(res.data.data || []);
    } catch (error) {
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    const timer = setTimeout(() => {
      fetchItems();
    }, 400);
    return () => clearTimeout(timer);
  }, [search, classFilter, token]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure? This will delete student records.")) {
      try {
        await axios.delete(`${server}/students/deleteById?studentId=${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("Student deleted successfully");
        fetchItems();
      } catch (error) {
        toast.error("Error deleting student");
      }
    }
  };

  // Unique Classes & Sections calculation
  const availableClasses = useMemo(() => {
    const set = new Set(["Pre", "Nursery", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"]);
    items.forEach((s) => {
      if (s.class) set.add(String(s.class));
    });
    return Array.from(set).sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return String(a).localeCompare(String(b));
    });
  }, [items]);

  const availableSections = useMemo(() => {
    const set = new Set();
    items.forEach((s) => {
      if (s.section) set.add(String(s.section));
    });
    return Array.from(set).sort();
  }, [items]);

  const classCounts = useMemo(() => {
    const counts = {};
    items.forEach((s) => {
      if (s.class) {
        const clsKey = String(s.class);
        counts[clsKey] = (counts[clsKey] || 0) + 1;
      }
    });
    return counts;
  }, [items]);

  // Client-side filtering for section, rollNo, admissionNo
  const filteredItems = useMemo(() => {
    return items.filter((s) => {
      const matchSection =
        !sectionFilter || String(s.section || "") === String(sectionFilter);

      const matchRollNo =
        !rollNoFilter.trim() ||
        String(s.rollNo || s.rollNumber || "").toLowerCase().includes(rollNoFilter.trim().toLowerCase());

      const matchAdmissionNo =
        !admissionNoFilter.trim() ||
        String(s.admissionNumber || "").toLowerCase().includes(admissionNoFilter.trim().toLowerCase());

      return matchSection && matchRollNo && matchAdmissionNo;
    });
  }, [items, sectionFilter, rollNoFilter, admissionNoFilter]);

  const hasActiveFilters =
    search !== "" ||
    classFilter !== "" ||
    sectionFilter !== "" ||
    rollNoFilter !== "" ||
    admissionNoFilter !== "";

  const handleClearFilters = () => {
    setSearch("");
    setClassFilter("");
    setSectionFilter("");
    setRollNoFilter("");
    setAdmissionNoFilter("");
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaUserGraduate size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Student Directory Master</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Manage student admissions, profiles, and class enrollments</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/my/students/register")}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaPlus size={10} /> Register New Student
          </button>
        </div>
      </div>

      {/* Quick KPI Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Total Enrolled Students</span>
          <div className="text-xl font-black text-indigo-600 dark:text-indigo-400 mt-0.5">{items.length}</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Filtered View</span>
          <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
            {filteredItems.length}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Directory Search</span>
          <div className="text-xs font-bold text-gray-700 dark:text-slate-300 mt-1 truncate">
            {search || "Showing All"}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Academic Year</span>
          <div className="text-sm font-black text-gray-900 dark:text-white mt-0.5">2026 - 2027</div>
        </div>
      </div>

      {/* Advanced Filtration Section */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4 flex flex-col gap-3">
        {/* Header of Filter Container */}
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-800 pb-2">
          <div className="flex items-center gap-2">
            <FaFilter className="text-indigo-600 dark:text-indigo-400 text-xs" />
            <span className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-wider">
              Student Directory Filters
            </span>
          </div>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="text-[11px] font-extrabold text-rose-500 dark:text-rose-400 hover:underline flex items-center gap-1"
            >
              <FaTimes size={11} /> Reset Filters
            </button>
          )}
        </div>

        {/* Quick Class Selector Pills */}
        <div>
          <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
            <FaGraduationCap size={10} /> Quick Class Filter
          </label>
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1.5 custom-scrollbar">
            <button
              type="button"
              onClick={() => setClassFilter("")}
              className={`px-3 py-1 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                classFilter === ""
                  ? "bg-indigo-600 text-white shadow-xs"
                  : "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700"
              }`}
            >
              All Classes ({items.length})
            </button>
            {availableClasses.map((cls) => (
              <button
                key={cls}
                type="button"
                onClick={() => setClassFilter(cls)}
                className={`px-3 py-1 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1 ${
                  classFilter === cls
                    ? "bg-indigo-600 text-white shadow-xs"
                    : "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700"
                }`}
              >
                {isNaN(cls) ? cls : `Class ${cls}`}
                {classCounts[cls] ? (
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                      classFilter === cls
                        ? "bg-white/20 text-white"
                        : "bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-300"
                    }`}
                  >
                    {classCounts[cls]}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        {/* 4-Column Filter Grid (Class, Section, Roll No, Reg No) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-gray-50/80 dark:bg-slate-800/40 p-3 rounded-2xl border border-gray-200/80 dark:border-slate-800">
          {/* Class Dropdown */}
          <div>
            <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
              <FaGraduationCap size={10} /> Class
            </label>
            <select
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-500"
            >
              <option value="">All Classes</option>
              {availableClasses.map((c) => (
                <option key={c} value={c}>
                  {isNaN(c) ? c : `Class ${c}`}
                </option>
              ))}
            </select>
          </div>

          {/* Section Dropdown */}
          <div>
            <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
              <FaLayerGroup size={10} /> Section
            </label>
            <select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-500"
            >
              <option value="">All Sections</option>
              {availableSections.map((sec) => (
                <option key={sec} value={sec}>
                  Section {sec}
                </option>
              ))}
            </select>
          </div>

          {/* Roll No Input */}
          <div>
            <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
              <FaHashtag size={10} /> Roll No
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="e.g. 12"
                value={rollNoFilter}
                onChange={(e) => setRollNoFilter(e.target.value)}
                className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-500"
              />
              {rollNoFilter && (
                <button
                  type="button"
                  onClick={() => setRollNoFilter("")}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 text-[10px]"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>

          {/* Admission No Input */}
          <div>
            <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
              <FaIdBadge size={10} /> Reg No
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="e.g. 1024"
                value={admissionNoFilter}
                onChange={(e) => setAdmissionNoFilter(e.target.value)}
                className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-500"
              />
              {admissionNoFilter && (
                <button
                  type="button"
                  onClick={() => setAdmissionNoFilter("")}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 text-[10px]"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Quick Search Bar (Placed Below Dropdowns) */}
        <div>
          <label className="block text-[10px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400 mb-1">
            Search Student Name or Details
          </label>
          <div className="relative">
            <FaSearch className="absolute left-3.5 top-3.5 text-gray-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search by Student Name, Reg No, Roll No..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900 focus:border-indigo-500 outline-none transition-all font-medium text-gray-800 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 text-xs shadow-xs"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-3.5 top-3 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        {/* Active Filter Chips Summary */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-1.5 p-2 bg-indigo-50/60 dark:bg-indigo-950/40 rounded-xl border border-indigo-100 dark:border-indigo-900/50 text-[11px]">
            <span className="font-extrabold text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
              <FaFilter size={9} /> Applied Filters:
            </span>
            {classFilter && (
              <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                Class: {classFilter}
                <button onClick={() => setClassFilter("")} className="hover:text-rose-500">
                  <FaTimes size={9} />
                </button>
              </span>
            )}
            {sectionFilter && (
              <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                Sec: {sectionFilter}
                <button onClick={() => setSectionFilter("")} className="hover:text-rose-500">
                  <FaTimes size={9} />
                </button>
              </span>
            )}
            {rollNoFilter && (
              <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                Roll: {rollNoFilter}
                <button onClick={() => setRollNoFilter("")} className="hover:text-rose-500">
                  <FaTimes size={9} />
                </button>
              </span>
            )}
            {admissionNoFilter && (
              <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                Reg: {admissionNoFilter}
                <button onClick={() => setAdmissionNoFilter("")} className="hover:text-rose-500">
                  <FaTimes size={9} />
                </button>
              </span>
            )}
            {search && (
              <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                "{search}"
                <button onClick={() => setSearch("")} className="hover:text-rose-500">
                  <FaTimes size={9} />
                </button>
              </span>
            )}
            <button
              onClick={handleClearFilters}
              className="font-extrabold text-rose-600 dark:text-rose-400 hover:underline ml-auto"
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Compact High-Density Student Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-8">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/80 text-gray-500 dark:text-slate-400 font-black text-[10px] uppercase border-b border-gray-200 dark:border-slate-800">
                <th className="p-2.5">Student Details</th>
                <th className="p-2.5">Admission No</th>
                <th className="p-2.5">Class & Section</th>
                <th className="p-2.5">Father / Guardian</th>
                <th className="p-2.5">Gender</th>
                <th className="p-2.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-indigo-600 font-bold">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    Loading student directory...
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-gray-400 italic">
                    No matching student records found.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/40 transition">
                    <td className="p-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-full bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-black text-xs flex items-center justify-center border border-indigo-200 dark:border-indigo-800 overflow-hidden shrink-0">
                          {item.studentPhoto ? (
                            <img src={item.studentPhoto} alt={item.studentName} className="h-full w-full object-cover" />
                          ) : (
                            item.studentName?.[0] || "S"
                          )}
                        </div>
                        <div>
                          <div className="font-black text-gray-900 dark:text-white text-xs">{item.studentName || "Unknown"}</div>
                          <div className="text-[10px] text-gray-400 font-medium">Roll No: {item.rollNo || item.rollNumber || "N/A"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-2.5 font-bold text-indigo-600 dark:text-indigo-400">{item.admissionNumber || "N/A"}</td>
                    <td className="p-2.5 font-bold text-gray-800 dark:text-slate-200">
                      Class {item.class || "N/A"} {item.section ? `(${item.section})` : ""}
                    </td>
                    <td className="p-2.5 font-semibold text-gray-700 dark:text-slate-300">{item.fatherName || "N/A"}</td>
                    <td className="p-2.5 font-medium text-gray-500 dark:text-slate-400">{item.gender || "N/A"}</td>
                    <td className="p-2.5 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => handleViewDetails(item.id)}
                          className="p-1.5 text-emerald-600 bg-emerald-50 dark:bg-emerald-950 rounded-lg hover:bg-emerald-100 transition"
                          title="View Profile"
                        >
                          <FaEye size={11} />
                        </button>
                        <button
                          onClick={() => navigate(`/my/students/edit/${item.id}`)}
                          className="p-1.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-950 rounded-lg hover:bg-indigo-100 transition"
                          title="Edit Student"
                        >
                          <FaEdit size={11} />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-1.5 text-rose-600 bg-rose-50 dark:bg-rose-950 rounded-lg hover:bg-rose-100 transition"
                          title="Delete Student"
                        >
                          <FaTrash size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Student Details Modal Overlay */}
      {isViewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs overflow-y-auto">
          <div className="relative w-full max-w-5xl my-8">
            {detailsLoading ? (
              <div className="bg-white dark:bg-slate-900 p-12 rounded-2xl text-center font-bold text-indigo-600 shadow-2xl border border-gray-200 dark:border-slate-800">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-3"></div>
                Loading student profile...
              </div>
            ) : (
              <StudentDetails
                student={studentDetails}
                studentDetails={studentDetails}
                onClose={() => setIsViewOpen(false)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
